/**
 * App-wide developer-tunable constants.
 * Add new constants here rather than scattering magic numbers through the codebase.
 */

/**
 * Convert a px design value to a `rem` string (base 16px), e.g.
 * `pxToRem(13)` → `'0.8125rem'`.
 *
 * Font sizes are authored in `rem` — not `px` — so text scales with the OS font
 * setting (iOS "Larger Text" / Android font size). WebKit inflates relative
 * units under Dynamic Type but leaves explicit `px` fixed, so a `px` font size
 * simply never grows. This helper lets a call site keep its original px design
 * value while emitting the scalable unit. (CSS files can't call it — write the
 * literal `rem` there instead.)
 */
export const pxToRem = (px: number): string => `${px / 16}rem`;

/**
 * How often (in ms) the app re-fetches `/api/config` (feature flags, Turnstile
 * key) while foregrounded. Gated on app-active state, so a hidden/backgrounded
 * tab does not poll — see AppConfigProvider. A returning foreground tab refetches
 * immediately rather than waiting out the interval.
 */
export const CONFIG_REFRESH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * How often (in seconds) the Snowball background sync runs. Each tick processes
 * up to CONTACT_SYNC_BATCH_SIZE contacts (oldest lastSnowballRefreshDate first).
 */
export const CONTACT_SYNC_INTERVAL_SECONDS = 60;

/**
 * How many contacts the Snowball background sync resolves per tick. Warming the
 * snowballId cache in small batches (rather than one at a time) lets the share
 * wizard's past-recipient match skip OPRF for already-resolved contacts, while
 * staying bounded so a tick's OPRF work never janks the foreground.
 */
export const CONTACT_SYNC_BATCH_SIZE = 3;

/**
 * How often (in seconds) the address-book server sync reconciles the local book
 * with the encrypted copy on the server (#476). Runs while the app is
 * foregrounded, in addition to firing on local edits and on foreground.
 */
export const ADDRESS_BOOK_SYNC_INTERVAL_SECONDS = 120;

/**
 * Debounce (in ms) applied to the address-book flush after a local edit, so a
 * burst of reducer changes coalesces into a single encrypted upload.
 */
export const ADDRESS_BOOK_SYNC_DEBOUNCE_MS = 2000;

/**
 * Test escape hatch: E2E tests can shorten the address-book sync timing so a
 * flush/reconcile fires almost immediately instead of waiting out the
 * production debounce/interval. Set the overrides in `Cypress.on('window:before:load')`:
 *
 *   win.__TEST_ADDRESS_BOOK_SYNC_DEBOUNCE_MS__ = 50;
 *   win.__TEST_ADDRESS_BOOK_SYNC_INTERVAL_SECONDS__ = 1;
 *
 * Production reads the constants above unchanged. The getters read the override
 * at call time so a value set before the bundle runs is picked up.
 */
type AddressBookSyncTimingTestWindow = Window & {
  __TEST_ADDRESS_BOOK_SYNC_DEBOUNCE_MS__?: number;
  __TEST_ADDRESS_BOOK_SYNC_INTERVAL_SECONDS__?: number;
};

function addressBookSyncTimingOverride(
  key: keyof AddressBookSyncTimingTestWindow
): number | undefined {
  if (typeof window === 'undefined') return undefined;
  const value = (window as AddressBookSyncTimingTestWindow)[key];
  return typeof value === 'number' ? value : undefined;
}

/** Effective flush debounce (ms) — production default unless a test overrides it. */
export function getAddressBookSyncDebounceMs(): number {
  return (
    addressBookSyncTimingOverride('__TEST_ADDRESS_BOOK_SYNC_DEBOUNCE_MS__') ??
    ADDRESS_BOOK_SYNC_DEBOUNCE_MS
  );
}

/** Effective reconcile interval (seconds) — production default unless a test overrides it. */
export function getAddressBookSyncIntervalSeconds(): number {
  return (
    addressBookSyncTimingOverride(
      '__TEST_ADDRESS_BOOK_SYNC_INTERVAL_SECONDS__'
    ) ?? ADDRESS_BOOK_SYNC_INTERVAL_SECONDS
  );
}

/**
 * Minimum gap (in ms) between "verify your phone to back up" toasts shown when an
 * unverified session changes the address book (#476), so repeated edits nag at
 * most once per minute.
 */
export const ADDRESS_BOOK_BACKUP_NAG_INTERVAL_MS = 60000;

/**
 * The Snowball ASCII logo, exposed to templates as a brand mark via the
 * `{_asciiLogo}` primitive.
 */
export const ASCII_LOGO = '°*❄️';

// Sheet breakpoints shared by the interstitial modals.
//
// The resting breakpoint is nudged up from the visually-natural ~0.5 so the
// primary ("Continue") button clears the iOS Safari bottom location bar on
// small screens. An Ionic sheet modal is pinned to the viewport bottom, so a
// taller sheet lifts the top-anchored content — and the button with it — above
// the bar's overlap zone. (A bottom margin can't do this: it only adds space
// below the button, it doesn't raise the button itself.)
export const INTERSTITIAL_BREAKPOINT = 0.55;
export const INTERSTITIAL_BREAKPOINTS = [0, INTERSTITIAL_BREAKPOINT, 1];

// A welcome sheet telling a news story opens taller, leaving room for the
// article below the relay intro.
export const INTERSTITIAL_BREAKPOINT_TALL = 0.75;
export const INTERSTITIAL_BREAKPOINTS_TALL = [
  0,
  INTERSTITIAL_BREAKPOINT_TALL,
  1,
];

// The made-up names in the hypothetical-reshare preview. It answers "what
// would the next person send", and both ends of that message are unknown — the person
// passing it on has not been written to yet, and whoever they pick is further
// off still — so the preview hydrates the template with made-up names rather
// than with real ones. A fresh pair each time the sheet is opened, because the
// names are the one part of the preview that is made up and names that rotate
// say so more plainly than a sober placeholder does.
//
// Kept as two lists so a sender is never also the recipient, and indexed in
// step: entry N of each is the pair shown on the Nth look. A preview several
// degrees removed also names the people who passed the message along in between
// from the sender list, so that list has more names than the deepest degree
// in PREVIEW_DEGREES and no one appears twice in one story.
export const HYPOTHETICAL_RECIPIENT_NAMES = [
  'Kai Rizzington',
  'Brynleigh Sidequest',
  'Tyler Nocap',
  'Maya Lowkey',
  'Jayden Mainchar',
  'Zoe Vibecheck',
];

export const HYPOTHETICAL_SENDER_NAMES = [
  'Finn Delulu',
  'Nia Slaymore',
  'Brody Bussin',
  'Luna Cheugy',
  'Ezra Bigyikes',
  'Priya Yeet',
  'Milo Periodt',
  'Harper Iykyk',
  'Owen Goated',
  'Sadie Snatched',
  'Theo Ratio',
  'Ivy Understood',
];

// The connections the made-up people in a hypothetical preview pick: the
// resharers in between, and the sender whose message is previewed. Their
// relationships are unknowable, so the preview makes up varied, specific ones
// rather than the plain "I know", which reads as filler — the point is to
// show what the connection field can say. First-person phrases, as a sender
// picks them; the server renders each in its third-person form when it tells
// the story, exactly as it renders a stored one, so each starts with "I " + a
// present-tense verb, "I'm" or "my". Keep the list length coprime with
// HYPOTHETICAL_CONNECTION_STRIDE in preview_logic.ts, and longer than the
// deepest degree in PREVIEW_DEGREES, so no story repeats one.
export const HYPOTHETICAL_CONNECTIONS: readonly string[] = [
  'I do yoga with',
  'I get coffee with',
  'I argue politics with',
  'I go thrifting with',
  'I go clubbing with',
  'my BFF',
  'I play music with',
  'I yap with',
  'my partner',
  'I play video games with',
  'I cook with',
  'I play word games with',
];

// The degrees the preview sheet offers: the message as it will be sent (0),
// then as the 1st, 2nd, 3rd and 10th person along would pass it on.
export const PREVIEW_DEGREES = [0, 1, 2, 3, 10] as const;

// A read-only preview sheet: two thirds, so the message it shows has room
// while the compose step it was opened from stays visible behind it — the
// point of the sheet is that the sender can see both. Draggable to full height
// for a long story, and down to dismiss.
export const PREVIEW_SHEET_BREAKPOINT = 0.67;
export const PREVIEW_SHEET_BREAKPOINTS = [0, PREVIEW_SHEET_BREAKPOINT, 1];

// The New Topic type picker: a sheet that stops just short of the top, so it
// reads as a sheet over the page it came from while giving the sticker
// carousel almost the whole screen. It has one resting height; drag down to
// dismiss.
export const TOPIC_TYPE_PICKER_SHEET_BREAKPOINT = 0.93;
export const TOPIC_TYPE_PICKER_SHEET_BREAKPOINTS = [
  0,
  TOPIC_TYPE_PICKER_SHEET_BREAKPOINT,
];

// Welcome's compact variant (Home-Screen-install section suppressed) uses a
// shorter sheet; nudged up from ~0.3 for the same reason.
export const INTERSTITIAL_BREAKPOINT_COMPACT = 0.4;
export const INTERSTITIAL_BREAKPOINTS_COMPACT = [
  0,
  INTERSTITIAL_BREAKPOINT_COMPACT,
];

/**
 * Character limit on the update summary a sender types in the share wizard's
 * Changes step (#427) — it must fit comfortably in a short text message.
 * Mirrors the backend's config.UpdateSummaryMaxLength.
 */
export const UPDATE_SUMMARY_MAX_LENGTH = 150;

/**
 * The fixed max-character-length tiers a topic manager can pick for any
 * free-text surface in the inline share-form builder — a standalone `input`
 * field and the nested "Other" write-in inside a `select`. Fixed tiers (rather
 * than a free number) keep the authored `maxlength` trivial to validate and the
 * picker a one-tap choice.
 */
export const FREE_TEXT_MAX_LENGTH_TIERS = [25, 50, 100, 150, 200] as const;

/** Human-readable size hints shown beside each tier in the picker. */
export const FREE_TEXT_MAX_LENGTH_HINTS: Readonly<Record<number, string>> = {
  25: 'a name',
  50: 'a phrase',
  100: 'a sentence',
  150: 'a thought',
  200: 'a paragraph',
};

/**
 * The most characters a leg's allowed detail may hold — the backend's
 * `maxAllowedDetailRunes`, which rejects a longer one. The detail is built from
 * the author's sentence around a sender's answer, so a long answer is shortened
 * to fit rather than failing the send.
 */
export const ALLOWED_DETAIL_MAX_LENGTH = 200;

/** Default limit for a standalone free-text field the author has not sized. */
export const FREE_TEXT_MAX_LENGTH_DEFAULT = 100;

/**
 * Default limit for a select's nested "Other" write-in. Lower than a standalone
 * field's: a write-in stands in for one of the preset option labels, so it is a
 * short phrase rather than free prose.
 */
export const OTHER_WRITE_IN_MAX_LENGTH_DEFAULT = 25;

/**
 * The text a fresh `goal` slot starts with in the share-form builder. Goal
 * slots are only offered while the topic has an impression goal, so the seeded
 * prose can assume both numbers resolve.
 */
export const GOAL_SLOT_DEFAULT_TEXT =
  'So far {_runImpressionPeople} have heard about this topic thru Snowball, ' +
  "but we're trying to reach {_impressionGoal} in record time.";

// Thresholds for the "significant change" heuristic (#427): edits crossing one
// of these get the automatic "Share Updates (recommended)" footer button. Any
// date or location change is significant regardless of size.
export const SIGNIFICANT_TITLE_CHANGE_CHARS = 5;
export const SIGNIFICANT_BODY_CHANGE_CHARS = 50;

// How much of a news topic's body travels with it: the SMS draft carries this
// many characters, and the news welcome sheet quotes the same excerpt.
export const NEWS_BODY_EXCERPT_CHARS = 150;

// Options for the "When did you first start using Snowball?" security question,
// one of two knowledge-based answers that can stand in for phone verification
// when a user who lost their SIM needs to re-verify their email while switching
// numbers (see PhoneMigrationQuestionsStep). The `value` is sent to the backend, which
// maps the account's creation date to the same buckets (iam.accountAgeBucket)
// and requires a match.
export const SECURITY_ACCOUNT_AGE_OPTIONS = [
  { value: 'days', label: 'Days ago' },
  { value: 'weeks', label: 'Weeks ago' },
  { value: 'months', label: 'Months ago' },
  { value: 'years', label: 'Years ago' },
] as const;

export type SecurityAccountAge =
  (typeof SECURITY_ACCOUNT_AGE_OPTIONS)[number]['value'];

// Mirror of the backend's idv.PhoneMigrationProofWindow (#441): how long a
// freshly earned migration proof (claims.migrationProofExp — correct security
// answers given while email-verified) authorizes switching phone numbers. It is
// also how long the short-window email claim from a claimless re-verify lasts.
// Used for user-facing copy; the actual gate reads the claim's own expiry.
export const PHONE_MIGRATION_PROOF_WINDOW_MS = 5 * 60 * 1000;

// How long the profile wizard's phone step waits after the last keystroke
// before looking up whether the entered number already has a profile (an OPRF
// round-trip), so the CTA can say honestly whether verifying will recover an
// existing profile or start a new one (#441).
export const PHONE_LOOKUP_DEBOUNCE_MS = 400;

// ── Footer dock controls (iOS 26 "liquid glass") ─────────────────────────────

// The floating footer-dock controls are frosted "liquid glass": a translucent
// fill plus a backdrop blur, so the scrolling page shows through softly.
// Two weights, tuned here:
// - SURFACE (the round pills/buttons — search pill, +, close, create, Device
//   Contact) sit directly over the list. Their fill is a slightly cool grey-blue
//   (like real iOS glass, never pure white) so they read as a distinct glassy
//   surface even over a white row — a pure-white translucent fill vanishes into
//   the white name column, which reads as "solid, no transparency".
// - CONTROL (the outline action buttons — Export/Import/return) carry a text
//   label, so they use a more opaque white fill to keep that label crisp.
// The SURFACE color is mirrored in footer.css (.footer-glass-round::before) for
// the native <button> controls — keep the two in sync.
export const FOOTER_SURFACE_BACKGROUND = 'rgba(240, 242, 248, 0.72)';
export const FOOTER_CONTROL_ALPHA = 0.85;
export const FOOTER_CONTROL_BACKGROUND = `rgba(255, 255, 255, ${FOOTER_CONTROL_ALPHA})`;
export const FOOTER_CONTROL_BACKDROP_BLUR = 'blur(20px)';

// Ready-to-spread frosted surface for a footer control rendered as a plain
// <div> element (the search pill): translucent cool fill + blur. A plain element
// blurs directly; the round IonButton controls use footerControlRoundButtonStyle
// instead (Safari won't blur a native <button>, so those go through Ionic).
export const footerControlSurfaceStyle = {
  backgroundColor: FOOTER_SURFACE_BACKGROUND,
  backdropFilter: FOOTER_CONTROL_BACKDROP_BLUR,
  WebkitBackdropFilter: FOOTER_CONTROL_BACKDROP_BLUR,
} as const;

// Frosted round/pill IonButton (+, close, create, add-contact, Device Contact):
// the cool-tint surface fill lives in the `--background` var. Pair with the
// `footer-glass-button footer-glass-round` classes (footer.css) — the first puts
// the backdrop blur on the button's native part (which carries the radius, so
// the frost clips to the shape), the second keeps it opaque when disabled so the
// scrim-less list can't bleed through. Per-button sizing (width/height,
// `--border-radius`, and any horizontal padding for a pill) is set at the call
// site.
export const footerControlRoundButtonStyle = {
  '--background': FOOTER_SURFACE_BACKGROUND,
  '--box-shadow': '0 14px 36px rgba(0, 0, 0, 0.12)',
  '--color': '#111',
  '--padding-top': '0',
  '--padding-bottom': '0',
  '--padding-start': '0',
  '--padding-end': '0',
  margin: 0,
} as const;

// The translucent fill for an outline IonButton, whose fill lives in the
// `--background` CSS var. Spread into the button's `style` and cast the result
// to CSSProperties at the call site (for the custom property). The backdrop
// blur can't ride here — on an IonButton it must sit on the native part so it
// clips to the rounded shape, so pair this with the `footer-glass-button` class
// (see footer.css). The primary filled CTA stays a solid filled button instead.
export const footerControlButtonStyle = {
  '--background': FOOTER_CONTROL_BACKGROUND,
} as const;

// ── Recipient-confirm polling (#388) ─────────────────────────────────────────
//
// The sender's confirm pages poll a leg to detect when a recipient taps the
// share link (useRecipientConfirm). A recipient is most likely to tap soon
// after the send, and senders often leave the tab open after being taken to
// their Messages app, so the poll interval backs off exponentially and the loop
// gives up entirely after an hour rather than running indefinitely.

// First re-poll delay; kept short so a click landing just after the sender's
// return is noticed quickly.
export const RECIPIENT_CONFIRM_POLL_INITIAL_INTERVAL_MS = 4000;

// Factor the poll interval grows by after each poll.
export const RECIPIENT_CONFIRM_POLL_BACKOFF_FACTOR = 1.5;

// Ceiling the backed-off poll interval never exceeds.
export const RECIPIENT_CONFIRM_POLL_MAX_INTERVAL_MS = 60_000;

// How long to keep polling before giving up. Measured as wall-clock from when
// polling starts, so time spent with the tab hidden still counts down.
export const RECIPIENT_CONFIRM_POLL_GIVE_UP_MS = 60 * 60 * 1000;

// ── Impression goal (#447) ───────────────────────────────────────────────

// Client-side mirror of the backend's DefaultImpressionGoalMax: the ceiling
// on any topic's impressionGoal when the impressionGoalMax config value
// (FEATURE_FLAGS_JSON, surfaced via /api/config) is unset.
export const IMPRESSION_GOAL_MAX_DEFAULT = 250;

// ── Screenful awareness lookups (#581) ───────────────────────────────────

// How long the recipient list waits after scrolling stops before asking the
// server which of the now-visible contacts have already seen the topic.
// Awareness costs OPRF hashing plus a round trip, so it is resolved a screenful
// at a time rather than for the whole address book; the pause keeps a fast
// flick through the list from firing a request per frame.
export const AWARENESS_SCROLL_IDLE_MS = 500;

// ── Group texts ──────────────────────────────────────────────────────────

// How many recipients one group text may carry. A share addressed to more than
// this fans out into several legs of at most this many people, which is what
// makes "Group text" read singular only while the whole share fits one leg.
export const MAX_GRP_TXT_RECIPIENTS = 10;

// How long a finger rests on an element before `useLongPress` takes it as a
// long-press, the way a message lifts to offer its actions in iOS.
export const LONG_PRESS_MS = 500;

// How far a finger can drift during that press before it reads as a scroll
// and the press is abandoned.
export const LONG_PRESS_SLOP_PX = 10;
